import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, Shield, HardHat, AlertTriangle, CheckCircle2, FileCheck, Users } from "lucide-react";
import { ParticlesBackground, MouseGlowEffect } from "@/components/effects";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState, useEffect } from "react";

const floatingIcons = [
  { icon: Shield, delay: 0, x: "10%", y: "20%" },
  { icon: HardHat, delay: 0.5, x: "85%", y: "15%" },
  { icon: FileCheck, delay: 1, x: "5%", y: "70%" },
  { icon: Users, delay: 1.5, x: "90%", y: "65%" },
];

const wordAnimation = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: "easeOut" as const,
    },
  }),
};

export function ImmersiveHero() {
  const { t } = useTranslation('home');
  const { language } = useLanguage();
  
  const headline = t('hero.headline1');
  const subHeadline = t('hero.headline2');
  const words1 = headline.split(" ");
  const words2 = subHeadline.split(" ");

  const whatsappMessage = language === 'he' 
    ? "שלום,%20אשמח%20לקבל%20פרטים%20נוספים"
    : "Hello,%20I'd%20like%20more%20information";

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background - Hidden on mobile - Optimized poster with WebP and proper sizing */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        poster="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1440&q=75&fm=webp&fit=crop"
        className="absolute inset-0 w-full h-full object-cover hidden md:block"
        src="/videos/hero-background.mp4"
        onError={(e) => {
          (e.target as HTMLVideoElement).style.display = 'none';
        }}
      />

      {/* Mobile Fallback Background with accessible alt via aria-label - Optimized with WebP */}
      <div 
        className="absolute inset-0 md:hidden bg-cover bg-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=70&fm=webp&fit=crop')"
        }}
        role="img"
        aria-label="Automated industrial warehouse safety management by Adam Safety - תמונת רקע של מפעל תעשייתי עם עובדים ומערכות בטיחות"
      />

      {/* Dark Overlay for readability - reduced opacity for better mobile contrast */}
      <div className="absolute inset-0 bg-primary/60 md:bg-primary/65" />
      
      {/* Mesh Gradient Overlay */}
      <div 
        className="absolute inset-0 opacity-60"
        style={{
          background: `
            radial-gradient(ellipse at 20% 30%, hsl(38 92% 50% / 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 20%, hsl(222 47% 30% / 0.2) 0%, transparent 40%),
            radial-gradient(ellipse at 60% 80%, hsl(38 92% 50% / 0.1) 0%, transparent 50%)
          `
        }}
      />
      
      {/* Animated Grid Pattern */}
      <div className="absolute inset-0 pattern-hero opacity-30" />
      
      {/* Particles */}
      <ParticlesBackground variant="hero" />
      
      {/* Mouse Glow */}
      <MouseGlowEffect color="hsl(38, 92%, 50%)" size={600} opacity={0.15} />
      
      {/* Floating Safety Icons */}
      {floatingIcons.map((item, index) => (
        <motion.div
          key={index}
          className="absolute hidden lg:flex items-center justify-center w-16 h-16 rounded-2xl bg-primary-foreground/5 border border-primary-foreground/10 backdrop-blur-sm"
          style={{ left: item.x, top: item.y }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ 
            opacity: 1, 
            scale: 1,
            y: [0, -15, 0],
          }}
          transition={{
            opacity: { delay: item.delay + 0.5, duration: 0.5 },
            scale: { delay: item.delay + 0.5, duration: 0.5 },
            y: {
              delay: item.delay + 1,
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            },
          }}
        >
          <item.icon className="w-7 h-7 text-accent/80" />
        </motion.div>
      ))}

      {/* Animated Orbs */}
      <motion.div 
        className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full bg-accent/5 blur-[150px]"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div 
        className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-accent/8 blur-[120px]"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Main Content - Centered */}
      <div className="container mx-auto px-4 md:px-8 relative z-10 text-center pt-32 pb-24">
        {/* Animated Badge */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 backdrop-blur-md mb-10"
        >
          <motion.div 
            className="w-2.5 h-2.5 rounded-full bg-accent"
            animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <span className="text-primary-foreground/90 text-sm font-semibold">{t('hero.badge')}</span>
        </motion.div>

        {/* Animated Headline */}
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-primary-foreground leading-[1.15] mb-6 md:mb-8">
          {/* SEO: Full descriptive H1 text for crawlers */}
          <span className="sr-only">אדם בטיחות – ייעוץ בטיחות, הדרכות וניהול סיכונים</span>
          <span aria-hidden="true" className="flex flex-wrap justify-center gap-x-2 sm:gap-x-4">
            {words1.map((word, i) => (
              <motion.span
                key={i}
                custom={i}
                variants={wordAnimation}
                initial="hidden"
                animate="visible"
              >
                {word}
              </motion.span>
            ))}
          </span>
          <span aria-hidden="true" className="flex flex-wrap justify-center gap-x-2 sm:gap-x-4 mt-2">
            {words2.map((word, i) => (
              <motion.span
                key={i}
                custom={i + words1.length}
                variants={wordAnimation}
                initial="hidden"
                animate="visible"
                className="text-gradient-accent"
              >
                {word}
              </motion.span>
            ))}
          </span>
        </h1>

        {/* Subtitle Section - Static + Dynamic */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mb-8 md:mb-12 space-y-4"
        >
          {/* Static Subtitle Line 1 - Primary message */}
          <p 
            className="text-lg sm:text-xl md:text-2xl text-white font-medium
                       leading-relaxed max-w-3xl mx-auto px-4 text-center"
            style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
            }}
          >
            {t('hero.staticSubtitle1')}
          </p>
          
          {/* Static Subtitle Line 2 - Value props with separators */}
          <p 
            className="text-sm sm:text-base md:text-lg text-white/70 
                       leading-relaxed max-w-4xl mx-auto px-4 text-center"
            style={{
              textShadow: '0 1px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
            {t('hero.staticSubtitle2')}
          </p>
          
          {/* Dynamic Rotating Line - Accent color */}
          <RotatingSubtitle 
            subtitles={t('hero.rotatingSubtitles', { returnObjects: true }) as string[]} 
          />
        </motion.div>

        {/* Animated Safety Transformation - Hidden on small mobile */}
        <motion.div
          className="hidden sm:flex flex-wrap justify-center gap-3 md:gap-6 mb-8 md:mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
        >
          <SafetyTransformCard 
            icon={AlertTriangle}
            successIcon={CheckCircle2}
            text={t('hero.transformation.riskIdentified')}
            successText={t('hero.transformation.riskManaged')}
            delay={1.2}
          />
          <SafetyTransformCard 
            icon={HardHat}
            successIcon={CheckCircle2}
            text={t('hero.transformation.workerUntrained')}
            successText={t('hero.transformation.workerCertified')}
            delay={1.6}
          />
          <SafetyTransformCard 
            icon={FileCheck}
            successIcon={CheckCircle2}
            text={t('hero.transformation.docMissing')}
            successText={t('hero.transformation.docComplete')}
            delay={2}
            className="hidden md:flex"
          />
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center mb-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.6 }}
        >
          <Link to="/contact">
            <Button variant="hero" size="xl" className="group text-lg w-full sm:w-auto">
              <Phone className="w-5 h-5" />
              {t('hero.cta')}
              <motion.span
                className={language === 'he' ? "mr-1" : "ml-1"}
                animate={{ x: language === 'he' ? [0, -5, 0] : [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                {language === 'he' ? '←' : '→'}
              </motion.span>
            </Button>
          </Link>
          <a
            href={`https://wa.me/972528300088?text=${whatsappMessage}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="whatsapp" size="xl" className="text-lg w-full sm:w-auto">
              <MessageCircle className="w-5 h-5" />
              {t('hero.whatsapp')}
            </Button>
          </a>
        </motion.div>

        {/* Subtext */}
        <motion.p 
          className="text-primary-foreground/50 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
        >
          {t('hero.ctaSubtext')}
        </motion.p>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.6 }}
      >
        <motion.div
          className="w-6 h-10 rounded-full border-2 border-primary-foreground/30 flex justify-center pt-2"
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <motion.div
            className="w-1.5 h-3 bg-accent rounded-full"
            animate={{ opacity: [1, 0.3, 1], y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
}

// Rotating Subtitle Component
const ROTATION_INTERVAL = 4000; // 4 seconds

interface RotatingSubtitleProps {
  subtitles: string[];
}

function RotatingSubtitle({ subtitles }: RotatingSubtitleProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!subtitles || subtitles.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % subtitles.length);
    }, ROTATION_INTERVAL);
    
    return () => clearInterval(interval);
  }, [subtitles]);

  if (!subtitles || subtitles.length === 0) return null;

  return (
    <div className="relative min-h-[60px] md:min-h-[70px] flex items-center justify-center mt-6">
      {/* SEO: All subtitles rendered for crawlers */}
      <div className="sr-only">
        {subtitles.map((subtitle, i) => (
          <p key={i}>{subtitle}</p>
        ))}
      </div>

      {/* Visible rotating subtitle - Accent/Orange color with elegant blur fade */}
      <AnimatePresence mode="wait">
        <motion.p
          key={currentIndex}
          initial={{ opacity: 0, y: 10, filter: 'blur(4px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          exit={{ opacity: 0, y: -10, filter: 'blur(4px)' }}
          transition={{ 
            duration: 0.7, 
            ease: [0.25, 0.46, 0.45, 0.94] 
          }}
          className="text-base sm:text-lg md:text-xl text-accent font-semibold
                     leading-relaxed max-w-3xl mx-auto px-4 text-center"
          style={{
            textShadow: '0 2px 12px rgba(249, 115, 22, 0.3)'
          }}
        >
          {subtitles[currentIndex]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

// Safety Transformation Card Component
interface SafetyTransformCardProps {
  icon: React.ComponentType<{ className?: string }>;
  successIcon: React.ComponentType<{ className?: string }>;
  text: string;
  successText: string;
  delay: number;
  className?: string;
}

function SafetyTransformCard({ icon: Icon, successIcon: SuccessIcon, text, successText, delay, className = "" }: SafetyTransformCardProps) {
  return (
    <motion.div 
      className={`relative flex items-center gap-2 md:gap-3 px-3 md:px-5 py-2 md:py-3 rounded-xl bg-primary-foreground/5 border border-primary-foreground/10 backdrop-blur-sm overflow-hidden min-w-[140px] md:min-w-[160px] ${className}`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.5 }}
    >
      {/* Risk State */}
      <motion.div
        className="flex items-center gap-2 md:gap-3"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0, x: -20 }}
        transition={{ delay: delay + 1.5, duration: 0.4 }}
      >
        <Icon className="w-4 h-4 md:w-5 md:h-5 text-red-400" />
        <span className="text-primary-foreground/80 text-xs md:text-sm font-medium">{text}</span>
      </motion.div>
      
      {/* Success State */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center gap-2 md:gap-3 px-3 md:px-5"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: delay + 1.9, duration: 0.4 }}
      >
        <SuccessIcon className="w-4 h-4 md:w-5 md:h-5 text-emerald-400" />
        <span className="text-primary-foreground/90 text-xs md:text-sm font-medium">{successText}</span>
      </motion.div>
    </motion.div>
  );
}
